import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  alpha,
  Grid,
  Card,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import { Add as AddIcon, PersonSearch as PersonSearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CandidateFilters from '../../components/Candidates/CandidateFilters';
import CandidateListItem from '../../components/Candidates/CandidateListItem';
import { Candidate, CandidateFilters as CandidateFiltersType, initialFilters } from '../../types/Candidate';
import { getCandidates, filterCandidates } from '../../services/candidateService';
import { useThemeContext } from '../../context/ThemeContext';

const CandidatesPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useThemeContext();
  const isDark = mode === 'dark';
  
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [filters, setFilters] = useState<CandidateFiltersType>(initialFilters);
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [sortBy, setSortBy] = useState<string>('appliedDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState<number | null>(null);
  
  const ITEMS_PER_PAGE = 10;
  
  useEffect(() => {
    // Load candidates on mount
    const data = getCandidates();
    setCandidates(data);
  }, []);
  
  useEffect(() => {
    // Filter and sort candidates when filters or sort options change
    const filtered = filterCandidates(filters);
    
    // Sort candidates
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any = a[sortBy as keyof Candidate];
      let bValue: any = b[sortBy as keyof Candidate];
      
      // Handle experience level sorting
      if (sortBy === 'experience') {
        const experienceOrder: { [key: string]: number } = {
          'entry': 1,
          'junior': 2,
          'mid': 3,
          'senior': 4,
          'lead': 5,
          'executive': 6,
        };
        
        aValue = experienceOrder[aValue];
        bValue = experienceOrder[bValue];
      }
      
      // Handle date sorting
      if (sortBy === 'appliedDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setFilteredCandidates(sorted);
    setPage(1); // Reset to first page when filters change
  }, [filters, sortBy, sortDirection, candidates]);
  
  const handleFilterChange = (newFilters: CandidateFiltersType) => {
    setFilters(newFilters);
  };
  
  const handleSortChange = (event: any) => {
    setSortBy(event.target.value as string);
  };
  
  const handleDirectionChange = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };
  
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };
  
  const handleAddCandidate = () => {
    navigate('/candidates/new');
  };
  
  const handleDeleteClick = (id: number) => {
    setCandidateToDelete(id);
    setDeleteConfirmOpen(true);
  };
  
  const handleDeleteConfirm = () => {
    if (candidateToDelete) {
      // In a real app, you would make an API call here
      const updatedCandidates = candidates.filter(c => c.id !== candidateToDelete);
      setCandidates(updatedCandidates);
    }
    setDeleteConfirmOpen(false);
    setCandidateToDelete(null);
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setCandidateToDelete(null);
  };
  
  // Calculate active filter count for the filter badge
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.status.length) count++;
    if (filters.experience.length) count++;
    if (filters.department.length) count++;
    if (filters.dateRange.start) count++;
    if (filters.dateRange.end) count++;
    return count;
  };
  
  // Calculate pagination
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedCandidates = filteredCandidates.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredCandidates.length / ITEMS_PER_PAGE);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            }
          }}
        >
          Candidates
        </Typography>
        {/* <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddCandidate}
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: 'none',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          Add Candidate
        </Button> */}
      </Box>
      
      {/* Filters */}
      <CandidateFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        activeFilterCount={getActiveFilterCount()}
      />
      
      {/* Results section */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body1" color="textSecondary">
          Showing {filteredCandidates.length > 0 ? startIndex + 1 : 0} - {Math.min(endIndex, filteredCandidates.length)} of {filteredCandidates.length} candidates
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150, mr: 2 }}>
            <InputLabel id="sort-select-label">Sort By</InputLabel>
            <Select
              labelId="sort-select-label"
              value={sortBy}
              label="Sort By"
              onChange={handleSortChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="appliedDate">Application Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="experience">Experience</MenuItem>
              <MenuItem value="rating">Rating</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            size="small"
            onClick={handleDirectionChange}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
          </Button>
        </Box>
      </Box>
      
      {/* Candidate list */}
      {paginatedCandidates.length > 0 ? (
        paginatedCandidates.map((candidate) => (
          <CandidateListItem
            key={candidate.id}
            candidate={candidate}
            onDelete={handleDeleteClick}
          />
        ))
      ) : (
        <Card 
          sx={{ 
            p: 5, 
            textAlign: 'center',
            backgroundColor: isDark 
              ? alpha(theme.palette.primary.main, 0.05)
              : alpha(theme.palette.primary.main, 0.02),
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 2,
          }}
        >
          <PersonSearchIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No candidates found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
            No candidates match your current filters. Try adjusting your search criteria.
          </Typography>
          {/* <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddCandidate}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Add Candidate
          </Button> */}
        </Card>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this candidate? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CandidatesPage;